import React, { useState, useEffect, useMemo } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import MainLayout from '../../layout/main-layout';
import DuoModal from '../../components/ui/duo-modal';
import { APP_NAME } from '../../utils';
import { useDataContext } from '../../context/DataContext';
import { formatDate } from '../../utils/functions';
import Add from './add';
import Update from './update';
import Delete from './delete';
import UpdateSubCategories from '../sub-categories/update';
import { useSelector } from 'react-redux';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const categorySlug = (nom) => (nom || '').replace(/\s/g, '_').toLowerCase();

const statusBadgeClass = (item) =>
  Number(item.private) === 0 ? 'duo-badge-pill duo-badge-pill--ok' : 'duo-badge-pill duo-badge-pill--off';

const statusLabel = (item) => (Number(item.private) === 0 ? 'Active' : 'Inactive');

const getSortValue = (item, key) => {
  if (key === 'createur') return item.createur_categorie?.name ?? '';
  if (key === 'slug') return categorySlug(item.nom);
  return item[key] ?? '';
};

export default function Categories() {
  const { categories, fetchCategories } = useDataContext();
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ search: '', date: '', status: '' });
  const [draftFilters, setDraftFilters] = useState({ date: '', status: '' });

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'nom', direction: 'asc' });
  const [isOpen, setIsOpen] = useState(false);
  const [subCategoryModal, setSubCategoryModal] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const mainCategories = useMemo(
    () => (categories || []).filter((c) => c.type_categorie === null),
    [categories]
  );

  useEffect(() => {
    if (categories?.length) {
      setIsLoading(false);
    }
  }, [categories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredCategories = useMemo(() => {
    return mainCategories.filter((item) => {
      const search = appliedFilters.search.trim().toLowerCase();
      if (search && !item.nom?.toLowerCase().includes(search)) {
        return false;
      }
      if (appliedFilters.status !== '' && Number(item.private) !== Number(appliedFilters.status)) {
        return false;
      }
      if (appliedFilters.date) {
        const itemDate = item.date_creation || item.created_at;
        if (!itemDate || new Date(itemDate).toDateString() !== new Date(appliedFilters.date).toDateString()) {
          return false;
        }
      }
      return true;
    });
  }, [mainCategories, appliedFilters]);

  const sortedCategories = useMemo(() => {
    const sorted = [...filteredCategories];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, key);
      const bValue = getSortValue(b, key);
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredCategories, sortConfig]);

  const stats = useMemo(
    () => ({
      total: filteredCategories.length,
      active: filteredCategories.filter((c) => Number(c.private) === 0).length,
      inactive: filteredCategories.filter((c) => Number(c.private) !== 0).length,
      subTotal: filteredCategories.reduce(
        (acc, c) => acc + (c.sous_category?.length || 0),
        0
      ),
    }),
    [filteredCategories]
  );

  const totalPages = Math.max(1, Math.ceil(sortedCategories.length / perPage));
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentCategories = sortedCategories.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      date: draftFilters.date,
      status: draftFilters.status,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters({ date: '', status: '' });
    setAppliedFilters({ search: '', date: '', status: '' });
    setCurrentPage(1);
    setSortConfig({ key: 'nom', direction: 'asc' });
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    await fetchCategories();
    setIsLoading(false);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Catégories', 14, 16);
    const tableRows = currentCategories.map((item) => [
      item.nom,
      categorySlug(item.nom),
      (item.sous_category || []).map((s) => s.nom).join(', ') || '—',
      formatDate(item.date_creation || item.created_at),
      item.createur_categorie?.name ?? '—',
      statusLabel(item),
    ]);
    doc.autoTable({
      head: [['Catégorie', 'Slug', 'Sous-catégories', 'Créée le', 'Créé par', 'Statut']],
      body: tableRows,
      startY: 24,
    });
    doc.save(`Catégories ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(
      currentCategories.map((item) => ({
        Catégorie: item.nom,
        Slug: categorySlug(item.nom),
        'Sous-catégories': (item.sous_category || []).map((s) => s.nom).join(', '),
        'Créée le': formatDate(item.date_creation || item.created_at),
        'Créé par': item.createur_categorie?.name,
        Statut: statusLabel(item),
      }))
    );
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Catégories');
    writeFile(workbook, `Catégories ${APP_NAME}.xlsx`);
  };

  const colSpan = isRole13 ? 6 : 7;

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Catalogue</p>
          <h1 className="duo-hero-title">Catégories</h1>
          <p className="duo-hero-sub mt-2 mb-0">Organisez vos catégories et sous-catégories produits.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Catégories (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Actives</p>
            <p className="duo-kpi-chip__value">{stats.active}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Inactives</p>
            <p className="duo-kpi-chip__value">{stats.inactive}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Sous-catégories</p>
            <p className="duo-kpi-chip__value">{stats.subTotal}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des catégories
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={() => window.print()} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setIsOpen(!isOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${isOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <button
                  type="button"
                  className="duo-btn duo-btn--primary"
                  onClick={() => setShowAddModal(true)}
                >
                  <i className="bi bi-plus" /> Nouveau
                </button>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher une catégorie..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {isOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <input
                    type="date"
                    className="duo-input"
                    title="Date de création"
                    value={draftFilters.date}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, date: e.target.value }))}
                  />
                  <select
                    className="duo-select"
                    value={draftFilters.status}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="0">Active</option>
                    <option value="1">Inactive</option>
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('nom')}>
                    Catégorie <SortIcon active={isSortActive('nom')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('slug')}>
                    Slug <SortIcon active={isSortActive('slug')} direction={sortConfig.direction} />
                  </th>
                  <th>Sous-catégories</th>
                  <th onClick={() => requestSort('date_creation')}>
                    Créée le <SortIcon active={isSortActive('date_creation')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('createur')}>
                    Créé par <SortIcon active={isSortActive('createur')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('private')}>
                    Statut <SortIcon active={isSortActive('private')} direction={sortConfig.direction} />
                  </th>
                  {!isRole13 && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {currentCategories.length > 0 ? (
                  currentCategories.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-bold">{item.nom}</td>
                      <td>{categorySlug(item.nom)}</td>
                      <td>
                        <button
                          type="button"
                          className="duo-btn duo-btn--ghost duo-btn--sm"
                          onClick={() => setSubCategoryModal(item)}
                        >
                          <span className="duo-badge-pill duo-badge-pill--info">
                            {(item.sous_category?.length || 0)} sous-cat.
                          </span>
                        </button>
                      </td>
                      <td className="text-nowrap">{formatDate(item.date_creation || item.created_at)}</td>
                      <td>{item.createur_categorie?.name ?? '—'}</td>
                      <td>
                        <span className={statusBadgeClass(item)}>{statusLabel(item)}</span>
                      </td>
                      {!isRole13 && (
                        <td>
                          <div className="duo-table__actions">
                            <button
                              type="button"
                              title="Modifier"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={() => setEditingCategory(item)}
                            >
                              <i className="bi bi-pencil-square" />
                            </button>
                            <button
                              type="button"
                              title="Supprimer"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={() => setDeletingCategory(item)}
                            >
                              <i className="bi bi-trash" style={{ color: '#ff4b4b' }} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="duo-empty-row">
                      Aucune catégorie correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentCategories.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedCategories.length)} sur{' '}
                {sortedCategories.length} entrée{sortedCategories.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      {subCategoryModal && (
        <DuoModal
          size="lg"
          title={`Sous-catégories — ${subCategoryModal.nom}`}
          onClose={() => setSubCategoryModal(null)}
          footer={
            <button type="button" className="duo-btn duo-btn--ghost" onClick={() => setSubCategoryModal(null)}>
              Fermer
            </button>
          }
        >
          {subCategoryModal.sous_category?.length > 0 ? (
            <div className="duo-detail-list">
              {subCategoryModal.sous_category.map((sub) => (
                <div key={sub.id} className="duo-detail-row">
                  <span className="duo-detail-row__label">{sub.nom}</span>
                  {!isRole13 && (
                    <button
                      type="button"
                      className="duo-btn duo-btn--ghost duo-btn--sm"
                      onClick={() => setEditingSubCategory(sub)}
                      title="Modifier"
                    >
                      <i className="bi bi-pencil-square" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="duo-empty-row mb-0">Aucune sous-catégorie pour cette catégorie.</p>
          )}
        </DuoModal>
      )}

      <Add open={showAddModal} onClose={() => setShowAddModal(false)} refreshData={handleRefresh} />
      <Update
        open={!!editingCategory}
        onClose={() => setEditingCategory(null)}
        category={editingCategory}
        refreshData={handleRefresh}
      />
      <Delete
        open={!!deletingCategory}
        onClose={() => setDeletingCategory(null)}
        category={deletingCategory}
        refreshData={handleRefresh}
      />
      <UpdateSubCategories
        open={!!editingSubCategory}
        onClose={() => setEditingSubCategory(null)}
        categories={categories}
        category={editingSubCategory}
        refreshData={handleRefresh}
      />
    </MainLayout>
  );
}
